<template>
    <div
        v-if="notice"
        class="offline-banner"
        :class="`offline-banner--${notice.tone}`"
        role="status"
    >
        {{ notice.message }}
        <button
            v-if="notice.action === 'signin'"
            type="button"
            class="offline-banner__action"
            @click="goToLogin"
        >
            Sign in
        </button>
    </div>
</template>

<script setup>
import { useAuthStore } from '@/stores/auth-store.js';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

const auth = useAuthStore();
const router = useRouter();

/**
 * One banner, three different truths.
 *
 * Saying "Offline" when the network is fine and the server is refusing sends the
 * operator to check a router that is working, and saying it when the session has
 * merely expired hides the one action that would fix it. `checking` renders
 * nothing at all: it is a boot state, not a fault, and a banner that appears
 * during every probe is how the old one came to flash on and off.
 */
const NOTICES = {
    offline: {
        tone: 'offline',
        message: 'Offline — showing saved data. Changes are disabled until the connection returns.',
    },
    'backend-unavailable': {
        tone: 'degraded',
        message: 'Can’t reach the server — showing saved data. Retrying automatically.',
    },
    'expired-session': {
        tone: 'auth',
        message: 'Your session has expired. Saved data is still available to view.',
        action: 'signin',
    },
};

const notice = computed(() => NOTICES[auth.getConnectivityStatus] ?? null);

const goToLogin = () => {
    router.push({ name: 'login' }).catch(() => {});
};
</script>

<style>
.offline-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    color: white;
    padding: 8px;
    text-align: center;
    z-index: 1000;
}

.offline-banner--offline {
    background: #a16d23;
}

.offline-banner--degraded {
    background: #9a3412;
}

.offline-banner--auth {
    background: #3730a3;
}

.offline-banner__action {
    margin-left: 10px;
    padding: 2px 10px;
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 4px;
    font-weight: 600;
    cursor: pointer;
}

.offline-banner__action:hover {
    background: rgba(255, 255, 255, 0.15);
}
</style>
